<template>
    <form-wrapper class="intake-review" @submit="handleFormSubmission(data)">
        <intake-form-section :title="$t('forms.intake.review_submit.sections.chief_complaint')">
            <p class="intake-review__hint">
                {{ $t('forms.intake.review_submit.fields.chief_complaint.hint') }}
            </p>
            <form-row>
                <form-cell>
                    <textarea-field
                        :placeholder="$t('forms.intake.review_submit.fields.chief_complaint.placeholder')"
                        :formData="data"
                        valuePath="chiefComplaint"
                        @change="change($event)"
                    ></textarea-field>
                </form-cell>
            </form-row>
        </intake-form-section>

        <section v-for="card in reviewCards" :key="card.key" class="intake-review__card">
            <div class="intake-review__card-header">
                <h3 class="intake-review__card-title">{{ card.title }}</h3>
                <button class="intake-review__edit" type="button" @click="editStep(card.step)">
                    {{ $t('forms.intake.review_submit.actions.edit') }}
                </button>
            </div>
            <p
                v-for="(line, index) in card.lines"
                :key="`${card.key}-${index}`"
                class="intake-review__line"
                :class="{ 'intake-review__line--muted': card.isEmpty }"
            >
                {{ line }}
            </p>
        </section>

        <template #footer>
            <common-buttons>
                <common-button
                    :label="$t('common.microcopy.back')"
                    type="secondary"
                    size="large"
                    :isSubmittable="false"
                    @click="goBack"
                ></common-button>
                <common-button
                    :label="$t('forms.intake.review_submit.actions.continue_to_sign')"
                    type="primary"
                    size="large"
                    :isProcessing="isSending"
                ></common-button>
            </common-buttons>
        </template>
    </form-wrapper>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import IntakeFormSection from 'components/forms/intake/IntakeFormSection'

    export default {
        mixins: [FormMixin],

        components: {
            IntakeFormSection,
        },

        props: {
            initialData: { type: Object, default: undefined },
            intakeData: { type: Object, required: true },
        },

        data() {
            return {
                data: {
                    chiefComplaint: undefined,
                    ...(this.initialData || {}),
                },
            }
        },

        computed: {
            reviewCards() {
                return [
                    {
                        key: 'about_you',
                        step: 1,
                        title: this.$t('forms.intake.review_submit.sections.about_you'),
                        ...this.aboutYouSummary,
                    },
                    {
                        key: 'accident',
                        step: 2,
                        title: this.$t('forms.intake.review_submit.sections.accident'),
                        ...this.accidentSummary,
                    },
                    {
                        key: 'how_you_felt',
                        step: 3,
                        title: this.$t('forms.intake.review_submit.sections.how_you_felt'),
                        ...this.howYouFeltSummary,
                    },
                    {
                        key: 'symptoms',
                        step: 4,
                        title: this.$t('forms.intake.review_submit.sections.current_symptoms'),
                        ...this.symptomsSummary,
                    },
                    {
                        key: 'health_screening',
                        step: 5,
                        title: this.$t('forms.intake.review_submit.sections.health_screening'),
                        ...this.healthScreeningSummary,
                    },
                    {
                        key: 'treatment',
                        step: 6,
                        title: this.$t('forms.intake.review_submit.sections.treatment'),
                        ...this.treatmentSummary,
                    },
                    {
                        key: 'providers',
                        step: 7,
                        title: this.$t('forms.intake.review_submit.sections.providers'),
                        ...this.providersSummary,
                    },
                    {
                        key: 'tests_imaging',
                        step: 8,
                        title: this.$t('forms.intake.review_submit.sections.tests_imaging'),
                        ...this.testsImagingSummary,
                    },
                    {
                        key: 'previous_injuries',
                        step: 9,
                        title: this.$t('forms.intake.review_submit.sections.previous_injuries'),
                        ...this.previousInjuriesSummary,
                    },
                    {
                        key: 'medical_surgical',
                        step: 10,
                        title: this.$t('forms.intake.review_submit.sections.medical_surgical'),
                        ...this.medicalSurgicalSummary,
                    },
                    {
                        key: 'medications',
                        step: 11,
                        title: this.$t('forms.intake.review_submit.sections.medications_allergies'),
                        ...this.medicationsSummary,
                    },
                    {
                        key: 'work_information',
                        step: 12,
                        title: this.$t('forms.intake.review_submit.sections.work_information'),
                        ...this.workInformationSummary,
                    },
                    {
                        key: 'social_family',
                        step: 13,
                        title: this.$t('forms.intake.review_submit.sections.social_family'),
                        ...this.socialFamilySummary,
                    },
                    {
                        key: 'daily_activities',
                        step: 14,
                        title: this.$t('forms.intake.review_submit.sections.daily_activities'),
                        ...this.dailyActivitiesSummary,
                    },
                    {
                        key: 'health_questions',
                        step: 15,
                        title: this.$t('forms.intake.review_submit.sections.health_questions'),
                        ...this.healthQuestionsSummary,
                    },
                ]
            },

            aboutYouSummary() {
                const about = this.intakeData.aboutYou || {}
                const lines = [about.fullName, about.dateOfBirth, about.phone, about.email].filter(Boolean)

                return this.toSummary(lines)
            },

            accidentSummary() {
                const accident = this.intakeData.whatHappened || {}
                const incidentType = accident.incidentType
                    ? this.$t(`forms.intake.what_happened.fields.incident_type.options.${accident.incidentType}`)
                    : null
                const location = [accident.city, accident.state].filter(Boolean).join(', ')
                const headline = [accident.incidentDate, incidentType].filter(Boolean).join(' · ')
                const lines = [headline, location, accident.description].filter(Boolean)

                return this.toSummary(lines)
            },

            howYouFeltSummary() {
                const felt = this.intakeData.howYouFelt || {}
                const immediate = felt.immediateSymptoms || []
                const delayed = felt.delayedSymptoms || []
                const lines = []

                immediate.forEach((symptom, index) => {
                    lines.push(this.formatSymptomLine(symptom, index, 'immediate'))
                })
                delayed.forEach((symptom, index) => {
                    lines.push(this.formatSymptomLine(symptom, index, 'delayed'))
                })

                if (felt.emsCalled) {
                    lines.push(
                        `${this.$t('forms.intake.how_you_felt.fields.ems_called.label')}: ${this.triStateLabel(felt.emsCalled)}`,
                    )
                }
                if (felt.erEvaluated) {
                    lines.push(
                        `${this.$t('forms.intake.how_you_felt.fields.er_evaluated.label')}: ${this.triStateLabel(
                            felt.erEvaluated,
                        )}`,
                    )
                }

                return this.toSummary(lines)
            },

            symptomsSummary() {
                const problems = this.intakeData.symptomsToday?.problems || []
                const lines = problems.map((problem, index) => {
                    const region = problem.bodyRegion
                        ? this.$t(`forms.intake.how_you_felt.fields.body_region.options.${problem.bodyRegion}`)
                        : this.$t('forms.intake.review_submit.unnamed_problem', {
                              number: index + 1,
                          })
                    const types = (problem.problemTypes || [])
                        .map((type) => this.$t(`forms.intake.symptoms_today.fields.problem_types.options.${type}`))
                        .join(', ')

                    return types ? `${region} — ${types}` : region
                })

                return this.toSummary(lines)
            },

            healthScreeningSummary() {
                const screening = this.intakeData.healthScreening || {}
                const fields = [
                    'bladderControlLoss',
                    'bowelControlLoss',
                    'groinNumbness',
                    'worseningWeakness',
                    'difficultyWalking',
                ]
                const positive = fields.filter((field) => screening[field] === 'yes')
                const answered = fields.some((field) => screening[field])

                if (!answered) {
                    return this.toSummary([])
                }

                if (!positive.length) {
                    return {
                        lines: [this.$t('forms.intake.review_submit.health_screening_clear')],
                        isEmpty: false,
                    }
                }

                const lines = positive.map((field) => this.$t(`forms.intake.health_screening.fields.${field}.label`))
                lines.unshift(
                    this.$t('forms.intake.review_submit.health_screening_positive', {
                        count: positive.length,
                    }),
                )

                return { lines, isEmpty: false }
            },

            treatmentSummary() {
                const treatments = this.intakeData.treatmentReceived?.treatments || []
                const lines = treatments.map((treatment, index) => {
                    if (treatment.treatmentType) {
                        return this.$t(`forms.intake.treatment_received.fields.treatment_type.options.${treatment.treatmentType}`)
                    }
                    return this.$t('forms.intake.review_submit.unnamed_treatment', {
                        number: index + 1,
                    })
                })

                return this.toSummary(lines)
            },

            providersSummary() {
                const providers = this.intakeData.treatingProviders?.providers || []
                const lines = providers.map((provider, index) => {
                    return (
                        provider.providerName ||
                        this.$t('forms.intake.review_submit.unnamed_provider', {
                            number: index + 1,
                        })
                    )
                })

                return this.toSummary(lines)
            },

            testsImagingSummary() {
                const tests = this.intakeData.testsImaging?.tests || []
                const lines = tests.map((test, index) => {
                    if (test.testType) {
                        const type = this.$t(`forms.intake.tests_imaging.fields.test_type.options.${test.testType}`)
                        const region = test.bodyRegion
                            ? this.$t(`forms.intake.how_you_felt.fields.body_region.options.${test.bodyRegion}`)
                            : null
                        return [type, region, test.date].filter(Boolean).join(' · ')
                    }
                    return this.$t('forms.intake.review_submit.unnamed_test', {
                        number: index + 1,
                    })
                })

                return this.toSummary(lines)
            },

            previousInjuriesSummary() {
                const injuries = this.intakeData.previousInjuries?.priorInjuries || []
                const lines = injuries.map((injury, index) => {
                    const type = injury.incidentType
                        ? this.$t(`forms.intake.what_happened.fields.incident_type.options.${injury.incidentType}`)
                        : this.$t('forms.intake.review_submit.unnamed_injury', {
                              number: index + 1,
                          })
                    return [type, injury.approximateDate, injury.description].filter(Boolean).join(' · ')
                })

                return this.toSummary(lines)
            },

            medicalSurgicalSummary() {
                const history = this.intakeData.medicalSurgical || {}
                const lines = []

                if (history.medicalConditionsStatus) {
                    lines.push(
                        this.$t(
                            `forms.intake.medical_surgical.fields.medical_conditions.options.${history.medicalConditionsStatus}`,
                        ),
                    )
                }

                ;(history.surgeries || []).forEach((surgery, index) => {
                    lines.push(
                        [surgery.procedure, surgery.bodyRegion, surgery.dateOrYear].filter(Boolean).join(' · ') ||
                            this.$t('forms.intake.review_submit.unnamed_surgery', {
                                number: index + 1,
                            }),
                    )
                })

                return this.toSummary(lines)
            },

            medicationsSummary() {
                const meds = this.intakeData.medicationsAllergies || {}
                const medicationLabel = meds.currentMedications
                    ? this.triStateLabel(meds.currentMedications)
                    : this.$t('forms.intake.review_submit.empty')
                const allergyLabel = meds.allergies
                    ? this.$t(`forms.intake.medications_allergies.fields.allergies.options.${meds.allergies}`)
                    : this.$t('forms.intake.review_submit.empty')

                return {
                    lines: [
                        this.$t('forms.intake.review_submit.medications_allergies_summary', {
                            medications: medicationLabel,
                            allergies: allergyLabel,
                        }),
                    ],
                    isEmpty: !meds.currentMedications && !meds.allergies,
                }
            },

            workInformationSummary() {
                const work = this.intakeData.workInformation || {}
                const lines = work.employmentStatus
                    ? [this.$t(`forms.intake.work_information.fields.employment_status.options.${work.employmentStatus}`)]
                    : []

                return this.toSummary(lines)
            },

            socialFamilySummary() {
                const social = this.intakeData.socialFamily || {}
                const lines = []

                if (social.smoking) {
                    lines.push(
                        `${this.$t('forms.intake.social_family.sections.smoking')}: ${this.$t(
                            `forms.intake.social_family.fields.smoking.options.${social.smoking}`,
                        )}`,
                    )
                }
                if (social.alcohol) {
                    lines.push(
                        `${this.$t('forms.intake.social_family.sections.alcohol')}: ${this.$t(
                            `forms.intake.social_family.fields.alcohol.options.${social.alcohol}`,
                        )}`,
                    )
                }
                if (social.drugUse) {
                    lines.push(
                        `${this.$t('forms.intake.social_family.sections.drug_use')}: ${this.$t(
                            `forms.intake.social_family.fields.drug_use.options.${social.drugUse}`,
                        )}`,
                    )
                }
                if (social.familyHistory) {
                    lines.push(
                        `${this.$t('forms.intake.social_family.sections.family_history')}: ${this.$t(
                            `forms.intake.social_family.fields.family_history.options.${social.familyHistory}`,
                        )}`,
                    )
                }

                return this.toSummary(lines)
            },

            dailyActivitiesSummary() {
                const activities = this.intakeData.dailyActivities || {}
                const activityFields = [
                    'walking',
                    'standing',
                    'sitting',
                    'bending',
                    'lifting',
                    'climbing_stairs',
                    'driving',
                    'sleeping',
                    'dressing',
                    'bathing',
                    'household_chores',
                    'shopping',
                    'exercise',
                    'work',
                    'childcare_caregiving',
                    'social_recreational',
                ]
                const limited = activityFields.filter((field) => {
                    const value = activities[field]
                    return value && value !== 'no_difficulty' && value !== 'not_applicable'
                })
                const devices = activities.assistiveDevices || []
                const lines = []

                if (limited.length) {
                    lines.push(
                        this.$t('forms.intake.review_submit.daily_activities_summary', {
                            count: limited.length,
                        }),
                    )
                    limited.slice(0, 6).forEach((field) => {
                        const label = this.$t(`forms.intake.daily_activities.fields.activities.${field}`)
                        const difficulty = this.$t(`forms.intake.daily_activities.fields.difficulty.options.${activities[field]}`)
                        lines.push(`${label}: ${difficulty}`)
                    })
                }

                if (devices.length) {
                    lines.push(
                        this.$t('forms.intake.review_submit.daily_activities_devices', {
                            count: devices.length,
                        }),
                    )
                    devices.forEach((device, index) => {
                        lines.push(
                            device.device
                                ? this.$t(`forms.intake.daily_activities.fields.device.options.${device.device}`)
                                : this.$t('forms.intake.review_submit.unnamed_device', {
                                      number: index + 1,
                                  }),
                        )
                    })
                }

                return this.toSummary(lines)
            },

            healthQuestionsSummary() {
                const questions = this.intakeData.healthQuestions || {}
                const present = Object.keys(questions).filter((key) => questions[key] === 'present')

                if (!Object.keys(questions).length) {
                    return this.toSummary([])
                }

                if (!present.length) {
                    return {
                        lines: [this.$t('forms.intake.review_submit.health_questions_summary', { count: 0 })],
                        isEmpty: false,
                    }
                }

                const lines = [
                    this.$t('forms.intake.review_submit.health_questions_summary', {
                        count: present.length,
                    }),
                    ...present.slice(0, 8).map((key) => this.$t(`forms.intake.health_questions.fields.${key}`)),
                ]

                return { lines, isEmpty: false }
            },
        },

        methods: {
            toSummary(lines) {
                if (!lines.length) {
                    return {
                        lines: [this.$t('forms.intake.review_submit.empty')],
                        isEmpty: true,
                    }
                }

                return { lines, isEmpty: false }
            },

            triStateLabel(value) {
                return this.$t(`forms.intake.what_happened.fields.tri_state.options.${value}`)
            },

            formatSymptomLine(symptom, index, timing) {
                const label =
                    symptom.symptom ||
                    this.$t('forms.intake.review_submit.unnamed_problem', {
                        number: index + 1,
                    })
                const region = symptom.bodyRegion
                    ? this.$t(`forms.intake.how_you_felt.fields.body_region.options.${symptom.bodyRegion}`)
                    : null
                const prefix =
                    timing === 'delayed'
                        ? this.$t('forms.intake.how_you_felt.sections.delayed_symptoms')
                        : this.$t('forms.intake.how_you_felt.sections.immediate_symptoms')

                return [prefix, label, region].filter(Boolean).join(' · ')
            },

            editStep(step) {
                this.emitEvent({ name: 'edit', payload: { step, data: this.data } })
            },

            goBack() {
                this.emitEvent({ name: 'back', payload: this.data })
            },

            handleFormSubmission(data) {
                this.isSending = true
                this.emitSent(data)
                this.isSending = false
            },
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_functions.scss';

    .intake-review__hint {
        margin: 0 0 rem(12px);
        font-size: rem($font-size--hint);
        line-height: 1.4;
        color: $color--txt-minor;
    }

    .intake-review__card {
        margin: 0 0 rem(16px);
        padding: rem(16px);
        border: rem(1px) solid $color--border-base;
        border-radius: rem($border-radius--base);
        background: #fff;
    }

    .intake-review__card-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: rem(12px);
        margin-bottom: rem(8px);
    }

    .intake-review__card-title {
        margin: 0;
        font-size: rem($font-size--form);
        font-weight: 700;
        color: $color--txt-header;
    }

    .intake-review__edit {
        padding: 0;
        border: 0;
        background: transparent;
        color: $color--accent-1;
        font-size: rem($font-size--form);
        font-weight: 600;
        cursor: pointer;

        &:hover {
            text-decoration: underline;
        }
    }

    .intake-review__line {
        margin: 0;
        font-size: rem($font-size--form);
        line-height: 1.45;
        color: $color--txt-base;

        & + & {
            margin-top: rem(4px);
        }

        &--muted {
            color: $color--txt-minor;
        }
    }
</style>
